import {startTransition} from 'react'
import {CircleIcon} from '@primer/octicons-react'
import {BranchName, NavList} from '@primer/react'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'
import {useFeatureState} from '@github-ui/pull-requests/use-feature-state'
import {FeatureState} from '@github-ui/pull-requests/page-data/payloads/header'

import styles from './StackList.module.css'
import type {StackSectionEntryType} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import {getStackEntryLineClass, getBaseRefLineClass} from '../../../helpers/get-stack-entry-line-class'
import {useStackRebaseStatus} from '../../../hooks/mutations/use-stack-rebase-mutation'
import {useMergeabilityLiveUpdates} from '../../../hooks/use-mergeability-live-updates'
import {useStackMergeabilityQuery} from '../../../page-data/loaders/use-stack-mergeability-page-data'
import {useMergeMethodContext} from '../../../contexts/MergeMethodContext'
import type {AliveChannels as MergeBoxAliveChannels} from '../../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../../page-data/payloads/status-checks'
import {EntryStatusIcon} from './EntryStatusIcon'
import {EntryStatusLabel} from './EntryStatusLabel'
import {StackEntryNavItem} from './StackEntryNavItem'

export function StackList({
  entries,
  baseRefName,
  currentPullRequestId,
}: {
  entries: StackSectionEntryType[]
  baseRefName?: string
  currentPullRequestId: string
}) {
  const {displayStatus: rebaseDisplayState} = useStackRebaseStatus()
  const pullRequestStacksEffectiveEntryStatusesEnabled =
    useFeatureState('pullRequestStacksEffectiveEntryStatuses') === FeatureState.Enabled
  const {pathname} = useLocation()
  const tabSuffix = getPullRequestTabSuffix(pathname)

  return (
    <NavList className={styles.stackNavList} aria-label="Stacked pull requests list">
      {entries.map((e, idx) => {
        const previousEntry = entries[idx - 1]
        if (pullRequestStacksEffectiveEntryStatusesEnabled) {
          return (
            <StackEntryNavItem
              key={e.pull.number}
              entry={e}
              previousEntry={previousEntry}
              tabSuffix={tabSuffix}
              currentPullRequestId={currentPullRequestId}
            />
          )
        }

        return (
          <NavList.Item
            key={e.pull.number}
            href={`${e.pull.url}${tabSuffix}`}
            aria-current={e.position === 'CURRENT'}
            className={getStackEntryLineClass(
              e,
              rebaseDisplayState[e.pull.number]?.state,
              previousEntry,
              previousEntry && rebaseDisplayState[previousEntry.pull.number]?.state,
            )}
          >
            <NavList.LeadingVisual>
              <EntryStatusIcon entry={e} rebaseState={rebaseDisplayState[e.pull.number]?.state} />
            </NavList.LeadingVisual>
            <span className={e.position === 'AFTER' ? 'fgColor-muted' : ''}>{e.pull.title}</span>
            <NavList.Description variant="block">
              <span className="fgColor-muted">
                #{e.pull.number} · {e.pull.headBranch}
              </span>
            </NavList.Description>
            <NavList.TrailingVisual>
              <EntryStatusLabel entry={e} rebaseState={rebaseDisplayState[e.pull.number]?.state} />
            </NavList.TrailingVisual>
            {!!e.channels && (
              <EntryMergeabilityUpdateListener
                basePageDataUrl={e.pull.url}
                currentPullRequestId={currentPullRequestId}
                {...e.channels}
              />
            )}
          </NavList.Item>
        )
      })}
      {baseRefName && entries.length > 0 && (
        <li className={`${styles.branchItem} ${getBaseRefLineClass(entries, rebaseDisplayState)}`}>
          <span className={styles.branchItemContent}>
            <CircleIcon size={12} className="fgColor-muted" />
            <BranchName as="span">{baseRefName}</BranchName>
          </span>
        </li>
      )}
    </NavList>
  )
}

function EntryMergeabilityUpdateListener({
  basePageDataUrl,
  currentPullRequestId,
  ...channels
}: {
  basePageDataUrl: string
  currentPullRequestId: string
} & MergeBoxAliveChannels &
  StatusChecksAliveChannels) {
  const {mergeMethod} = useMergeMethodContext()
  const {refetch} = useStackMergeabilityQuery({basePageDataUrl, currentPullRequestId, mergeMethod, enabled: false})
  useMergeabilityLiveUpdates({
    channels,
    refetchQuery: () => {
      startTransition(() => {
        refetch()
      })
    },
    throttleTime: 5000,
  })
  return null
}
